import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Users, BookOpen, ClipboardList, Trophy,
  LogOut, Menu, X, ChevronRight, Settings, ShieldCheck, FileText
} from 'lucide-react';
import toast from 'react-hot-toast';

// All nav items — always visible to every admin (original behaviour)
const NAV_ITEMS = [
  { to: '/admin/dashboard',     icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/students',      icon: Users,           label: 'Students' },
  { to: '/admin/questions',     icon: BookOpen,        label: 'Questions' },
  { to: '/admin/tests',         icon: ClipboardList,   label: 'Tests' },
  { to: '/admin/results',       icon: FileText,        label: 'Results' },
  { to: '/admin/leaderboard',   icon: Trophy,          label: 'Leaderboard' },
  { to: '/admin/exam-settings', icon: Settings,        label: 'Exam Settings' },
];

// Superadmin-only nav items
const SUPER_NAV_ITEMS = [
  { to: '/admin/manage-admins', icon: ShieldCheck, label: 'Manage Admins' },
];

export default function AdminLayout() {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const superadmin = isSuperAdmin();

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Sidebar ────────────────────────────────────────────────────────── */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 flex flex-col transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>

        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <div className="w-14 h-14 flex items-center justify-center bg-white rounded-xl overflow-hidden shrink-0 shadow-sm border border-gray-100">
            <img src="/bits-logo.png" alt="BITS Logo" className="w-12 h-12 object-contain" />
          </div>
          <div className="min-w-0">
            <h1 className="font-extrabold text-gray-900 text-sm leading-tight">BITSVIZAG</h1>
            <p className="font-bold text-yellow-600 text-xs leading-tight tracking-wide">EAMCET PORTAL</p>
            <p className="text-xs text-gray-400 mt-0.5">Admin Panel</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="ml-auto lg:hidden text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3 bg-yellow-50 rounded-xl p-3">
            <div className="w-9 h-9 rounded-full bg-yellow-400 flex items-center justify-center font-bold text-sm shrink-0 text-black">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{user?.name}</p>
              <p className="text-xs font-semibold text-yellow-700 truncate">
                {superadmin ? 'Super Admin' : 'Admin'}
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
          {/* Standard tabs — always visible */}
          {NAV_ITEMS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-150 group ${
                  isActive
                    ? 'bg-yellow-400 text-black shadow-sm shadow-yellow-200'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <Icon size={18} />
              <span className="flex-1">{label}</span>
              <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
            </NavLink>
          ))}

          {/* Superadmin-only section */}
          {superadmin && (
            <>
              <div className="pt-3 pb-1 px-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Super Admin</p>
              </div>
              {SUPER_NAV_ITEMS.map(({ to, icon: Icon, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-150 group ${
                      isActive
                        ? 'bg-yellow-400 text-black shadow-sm shadow-yellow-200'
                        : 'text-yellow-700 hover:bg-yellow-50 hover:text-yellow-900'
                    }`
                  }
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={18} />
                  <span className="flex-1">{label}</span>
                  <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                </NavLink>
              ))}
            </>
          )}

        </nav>

        {/* Logout */}
        <div className="px-4 pb-6">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all duration-150 font-medium text-sm"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* ── Main Content ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Topbar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <Menu size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-gray-900">Admin Dashboard</h2>
            <p className="text-xs text-gray-500">EAMCET Mock Test Management System</p>
          </div>
          {/* Role badge */}
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
            {superadmin ? 'Super Admin' : 'Admin'}
          </span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
